import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FilterField,
  FilterPanel,
  FormModal,
  InlineLoading,
  Page,
  ResponsiveGrid,
  Select,
  Table,
  Text,
  type Column,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { useTableSort, useUrlFilters } from "@marekl/adminer/hooks";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  canManageCompetition,
  canAdministerCompetitions,
  canViewCompetition,
  useMe,
} from "../../../auth/queries";
import { useSeason } from "../../../season";
import { useRuleSets } from "../../../lib/queries/rules";
import { RowLink } from "../../../components/RowLink";
import { BadgeRow } from "../../../components/BadgeRow";
import styles from "./CompetitionsPage.module.css";
import {
  useCompetitions,
  useCompetitionSeries,
  useCreateCompetition,
  useCreateCompetitionSeries,
  type Competition,
  type CompetitionType,
} from "../../../lib/queries/competitions";
import {
  COMPETITION_STATUS_LABELS,
  COMPETITION_STATUS_VARIANT,
  COMPETITION_TYPE_LABELS,
  ENTRY_PHASE_LABELS,
  ENTRY_PHASE_VARIANT,
  formatCompetitionDates,
} from "../../../lib/competitionState";
import {
  CompetitionFormFields,
  canSubmitCompetitionForm,
  competitionFormPayload,
  emptyCompetitionForm,
  type CompetitionFormState,
} from "../../../components/CompetitionForm";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }


export function CompetitionsPage() {
  const { data: me } = useMe();
  // The competition office creates and deletes; an organizer only manages
  // the competitions their assignment names — mirror rbac.CanManageCompetition
  // per row so the list never offers an action the server would refuse.
  const isGlobalManager = canAdministerCompetitions(me);
  // In the URL, so a filtered calendar is a link somebody can send. Season
  // is deliberately NOT here — it comes from the topbar switcher, one
  // season control per app.
  const [searchParams, setSearchParams] = useSearchParams();
  const { setFilter } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "page",
  });
  const typeFilter = searchParams.get("type") ?? "";

  // Which season's calendar is on screen comes from the topbar switcher,
  // not from a select of this page's own: one season control per app.
  // It is the "writable" flavour — a calendar is built before its season
  // goes active, and the backend puts no active-season constraint on
  // creating a competition.
  // isSelectedPast, not !isActiveSelected: this page deliberately writes
  // outside the active season — a calendar is built months before its
  // season goes active — but never BEHIND it. The server refuses that
  // (competition.ErrSeasonInPast); hiding the button keeps the refusal
  // from being the first the admin hears of it.
  const { selectedSeason, isSelectedPast } = useSeason();
  const seasonId = selectedSeason?.id;

  const {
    data: competitions = [],
    isLoading,
    error,
    refetch,
  } = useCompetitions(
    {
      season_id: seasonId,
      type: (typeFilter || undefined) as CompetitionType | undefined,
    },
    // Not until the season is known. Without the gate the first render asks
    // for competitions with no season_id, which the endpoint answers with
    // EVERY season's, so the table paints last season's calendar under a
    // chip naming this one and then settles — and a page whose picker
    // claims to state the season it is showing must not do that.
    { enabled: seasonId !== undefined },
  );
  const { data: ruleSets = [] } = useRuleSets();
  const { data: series = [], isLoading: seriesLoading } = useCompetitionSeries(seasonId);

  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [form, setForm] = useState<CompetitionFormState>(emptyCompetitionForm);

  const create = useCreateCompetition();
  const createSeries = useCreateCompetitionSeries();

  const isWriting = create.isPending || createSeries.isPending;
  const writeError = create.error || createSeries.error;

  // Only active sets can be published against; drafts of the next
  // season stay out of the picker to prevent a dead-end publish.
  const activeRuleSets = useMemo(
    () => ruleSets.filter((r) => r.status === "active"),
    [ruleSets],
  );

  const close = () => {
    setModal({ kind: "closed" });
    setForm(emptyCompetitionForm);
    create.reset();
    createSeries.reset();
  };

  const openCreate = () => {
    setForm(emptyCompetitionForm);
    create.reset();
    setModal({ kind: "create" });
  };

  const submit = async () => {
    if (!seasonId || !form.date) return;
    try {
      let seriesId: ID | null =
        form.seriesId && form.seriesId !== "new" ? form.seriesId : null;
      if (form.seriesId === "new" && form.newSeriesName.trim()) {
        const created = await createSeries.mutateAsync({
          season_id: seasonId,
          name: form.newSeriesName.trim(),
        });
        seriesId = created.id;
        // Write the created id back into the form: if the competition
        // save below fails, a retry must reuse this series instead of
        // re-posting it into the unique-name 409.
        setForm((f) => ({ ...f, seriesId: String(created.id), newSeriesName: "" }));
      }
      await create.mutateAsync(competitionFormPayload(form, seasonId, seriesId));
      close();
    } catch {
      // The mutation error renders in the modal alert.
    }
  };

  // Hoisted for useTableSort, which re-sorts on the columns identity.
  const columns = useMemo<Column<Competition>[]>(
    () => [
            {
              key: "name",
              header: "Název",
              sortable: true,
              sortValue: (c) => c.name,
              render: (c) => (
                <>
                  <div>
                    <RowLink to={`/soutez/${c.id}`}>{c.name}</RowLink>
                  </div>
                  {c.series_name && <Text variant="muted">{c.series_name}</Text>}
                </>
              ),
            },
            {
              key: "date",
              header: "Datum",
              sortable: true,
              // The ISO string, not the rendered range: `dateRange` composes
              // "12.03.2027 – 13.03.2027", which sorts by day-of-month.
              sortValue: (c) => c.date,
              render: (c) => formatCompetitionDates(c),
            },
            {
              key: "type",
              header: "Typ",
              sortable: true,
              sortValue: (c) => COMPETITION_TYPE_LABELS[c.type],
              render: (c) => COMPETITION_TYPE_LABELS[c.type],
            },
            {
              key: "rules",
              header: "Sada pravidel",
              render: (c) => <Text variant="muted">{c.rule_set_name}</Text>,
            },
            {
              key: "status",
              header: "Stav",
              sortable: true,
              // The lifecycle word the badge leads with, not the entry
              // phase beside it — the column is headed "Stav".
              sortValue: (c) => COMPETITION_STATUS_LABELS[c.status],
              render: (c) => (
                <BadgeRow>
                  <Badge variant={COMPETITION_STATUS_VARIANT[c.status]}>
                    {COMPETITION_STATUS_LABELS[c.status]}
                  </Badge>
                  {c.status === "published" && (
                    <Badge variant={ENTRY_PHASE_VARIANT[c.entry_phase]}>
                      {ENTRY_PHASE_LABELS[c.entry_phase]}
                    </Badge>
                  )}
                </BadgeRow>
              ),
            },
            {
              key: "actions",
              header: "",
              // Only the two doors: publishing lives in the lifecycle
              // control and deleting in the settings' danger zone.
              //
              // „Upravit" goes to the competition's Nastavení, not to the
              // create dialog. The dialog carries what a competition needs
              // to EXIST; settings carries the rest — vypsané disciplíny,
              // uzávěrka hudby, organizers, the lifecycle, the danger
              // zone — and editing through a form that shows a third of
              // the record invites the reader to think that is all of it.
              render: (c) =>
                canManageCompetition(me, c.id) ? (
                  <Table.Actions>
                    <Table.Action appearance="button" to={`/soutez/${c.id}`}>Řídit</Table.Action>
                    <Table.Action appearance="button" to={`/soutez/${c.id}/nastaveni`}>
                      Upravit
                    </Table.Action>
                  </Table.Actions>
                ) : canViewCompetition(me, c.id) ? (
                  // Čtení soutěží: the same workspace, every write switched off.
                  <Table.Actions>
                    <Table.Action appearance="button" to={`/soutez/${c.id}`}>Zobrazit</Table.Action>
                  </Table.Actions>
                ) : null,
            },
    ],
    [me],
  );

  const tableSort = useTableSort(competitions, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "date", dir: "asc" },
  });

  return (
    <>
      <AppTopbar seasonSwitcher="writable" crumbs={[{ label: "Soutěže" }]} />
      <Page>
        <Page.Header
          title="Soutěže"
          subtitle="zakládání a správa soutěží sezóny"
          actions={
            isGlobalManager &&
            !isSelectedPast && (
              <Button variant="primary" onClick={openCreate}>
                Nová soutěž
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          <FilterPanel>
            <FilterField>
              <Select
                label="Typ"
                value={typeFilter}
                onChange={(e) => setFilter("type", e.target.value)}
              >
                <option value="">Všechny typy</option>
                {Object.entries(COMPETITION_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FilterField>
          </FilterPanel>

          <Table<Competition>
            framed
            {...tableSort.tableProps}
            rowKey={(c) => c.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="Žádné soutěže"
                description="V této sezóně zatím není založena žádná soutěž."
              />
            }
            columns={columns}
          />

          {/* Read-only series overview: the season's championship at a
              glance — which rounds belong together and where they stand. */}
          {/* The rounds are already on screen saying they belong to a série,
              so the section is expected: while the names load it says so
              rather than appearing out of nowhere a moment later. */}
          {seriesLoading && competitions.some((c) => c.series_id) && (
            <>
              <Page.SectionHeader title="Série" />
              <InlineLoading>Načítám série…</InlineLoading>
            </>
          )}
          {series.length > 0 && competitions.some((c) => c.series_id) && (
            <>
              <Page.SectionHeader title="Série" />
              <ResponsiveGrid columns={2}>
                {series
                  .filter((s) => competitions.some((c) => c.series_id === s.id))
                  .map((s) => {
                    const members = competitions
                      .filter((c) => c.series_id === s.id)
                      .sort((a, b) => a.date.localeCompare(b.date));
                    return (
                      <Card key={s.id}>
                        <Card.Header
                          title={s.name}
                          subtitle={`${members.length} ${pluralizeCzech(members.length, ["kolo", "kola", "kol"])}`}
                          subtitlePlacement="right"
                        />
                        <Card.Content>
                          {members.map((c) => (
                            <div key={c.id} className={styles.seriesRound}>
                              <span>
                                {c.name} <Text variant="muted">{formatCompetitionDates(c)}</Text>
                              </span>
                              <Badge variant={COMPETITION_STATUS_VARIANT[c.status]}>
                                {COMPETITION_STATUS_LABELS[c.status]}
                              </Badge>
                            </div>
                          ))}
                        </Card.Content>
                      </Card>
                    );
                  })}
              </ResponsiveGrid>
            </>
          )}
        </Page.Content>
      </Page>

      <FormModal
        open={modal.kind === "create"}
        title="Nová soutěž"
        submitLabel="Vytvořit"
        busy={isWriting}
        canSubmit={canSubmitCompetitionForm(form)}
        error={writeError}
        onSubmit={submit}
        onClose={close}
        width="lg"
      >
        {/* A new competition is always a draft, so nothing here is ever
            locked — the „už zveřejněná" case belongs to the competition's
            own Nastavení, which is where editing happens now. */}
        <CompetitionFormFields
          form={form}
          onChange={setForm}
          ruleSets={activeRuleSets}
          series={series}
          lockRefs={false}
        />
      </FormModal>
    </>
  );
}
